import {
  PHASES,
  countryByCode,
  formatClock,
  formatTimeOfDay,
  type BroadcastState,
  type BroadcastTick,
  type CommentaryIntensity,
  type GlobalStats,
  type TickerItem,
} from '@fbwl/shared';
import { DESIGN } from '../core/runtime.js';

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`HUD element #${id} is missing from the document`);
  return node as T;
}

const TICKER_SPEED = 90; // design pixels per second

/**
 * The broadcast chrome.
 *
 * DOM rather than canvas, for three practical reasons: text stays vector-sharp
 * when OBS captures at 4K, `backdrop-filter` gives real glass for free, and the
 * layout survives a resize without re-measuring a single sprite.
 *
 * The whole thing is authored at 1920x1080 and scaled with one transform, so
 * every number in the stylesheet is a broadcast-safe pixel at any output size.
 */
export class Hud {
  private readonly root = el<HTMLDivElement>('hud');
  private readonly fps = el('hud-fps');
  private readonly clock = el('hud-clock');
  private readonly round = el('hud-round');
  private readonly phaseTimer = el('hud-phase-timer');
  private readonly season = el('hud-season');
  private readonly aliveCount = el('hud-alive');
  private readonly phaseRail = el('hud-phase-rail');
  private readonly statsStrip = el('hud-stats');
  private readonly championList = el('hud-champions');
  private readonly tickerTrack = el('hud-ticker');
  private readonly subtitle = el('hud-subtitle');
  private readonly pausedVeil = el('hud-paused');

  private readonly scorebug = el('hud-scorebug');
  private readonly homeCode = el('scorebug-home-code');
  private readonly homeName = el('scorebug-home-name');
  private readonly homeScore = el('scorebug-home-score');
  private readonly awayCode = el('scorebug-away-code');
  private readonly awayName = el('scorebug-away-name');
  private readonly awayScore = el('scorebug-away-score');
  private readonly roundLabel = el('scorebug-round');
  private readonly momentum = el('scorebug-momentum');

  private readonly royaleRound = el('royale-round');
  private readonly royaleRoundNo = el('royale-round-no');
  private readonly royaleRoundLabel = el('royale-round-label');
  private readonly royaleSessionTimer = el('royale-session-timer');
  private readonly royaleBoard = el('royale-board');
  private readonly royaleBoardTitle = el('royale-board-title');
  private readonly royaleBoardList = el('royale-board-list');
  private readonly royaleFlagsFill = el('royale-flags-fill');
  private readonly royaleFlagsAlive = el('royale-flags-alive');
  private readonly royaleFlagsTotal = el('royale-flags-total');
  private readonly royaleNext = el('royale-next');
  private readonly royaleNextTimer = el('royale-next-timer');

  private readonly pips: HTMLElement[] = [];
  private readonly tickerItems: TickerItem[] = [];
  private readonly tickerNodes: HTMLElement[] = [];

  private tickerOffset = 0;
  private phaseRemainingMs = 0;
  private lastPhaseId = '';
  private lastHomeScore = 0;
  private lastAwayScore = 0;
  private subtitleTimer = 0;

  /**
   * Snapshots only arrive on a phase change, so the totals inside them are the
   * totals as of the start of the phase. These offsets carry live events on top
   * of that baseline: without them the ELIMINATIONS counter reads zero for the
   * entire half hour in which 124 nations are eliminated.
   */
  private stats: GlobalStats | null = null;
  private eliminationOffset = 0;
  private matchOffset = 0;

  /** Locally ticked so the royale clocks stay smooth between snapshots. */
  private sessionRemainingMs = 0;
  private nextSessionMs: number | null = null;
  private flagsTotal = 0;

  constructor(visible = true) {
    for (let i = 0; i < PHASES.length; i++) {
      const pip = document.createElement('div');
      pip.className = 'phase-pip';
      this.phaseRail.appendChild(pip);
      this.pips.push(pip);
    }

    this.applyScale();
    window.addEventListener('resize', () => this.applyScale());

    if (!visible) this.root.style.display = 'none';
  }

  ready(): void {
    this.root.classList.add('ready');
  }

  setFps(value: number): void {
    this.fps.textContent = String(Math.round(value));
  }

  setState(state: BroadcastState): void {
    document.body.classList.toggle('mode-royale', state.mode === 'battle_royale');
    document.body.classList.toggle('mode-tournament', state.mode === 'tournament');

    this.season.textContent = String(state.season.number).padStart(3, '0');
    this.round.textContent = state.phase.label.toUpperCase();
    this.aliveCount.textContent = String(state.alive.length);
    this.pausedVeil.classList.toggle('visible', state.status === 'paused');
    this.renderRoyale(state.royale);

    if (state.phase.id !== this.lastPhaseId) {
      this.lastPhaseId = state.phase.id;
      this.updatePhaseRail(state.phase.order);
    }

    // A fresh snapshot already includes everything that has happened, so the
    // live offsets start again from zero.
    this.stats = state.stats;
    this.eliminationOffset = 0;
    this.matchOffset = 0;

    this.renderStats();
    this.renderChampions(state);
    this.syncTicker(state.ticker);
    this.renderMatch(state);
  }

  setTick(tick: BroadcastTick): void {
    this.phaseRemainingMs = tick.phaseRemainingMs;
    this.aliveCount.textContent = String(tick.aliveCount);
    this.setFlagsRemaining(tick.aliveCount);

    const current = this.pips.find((pip) => pip.classList.contains('current'));
    if (current)
      current.style.setProperty('--pip-progress', `${(tick.phaseProgress * 100).toFixed(1)}%`);

    if (!tick.match) {
      this.scorebug.classList.remove('visible');
      return;
    }

    this.scorebug.classList.add('visible');
    this.homeScore.textContent = String(tick.match.homeScore);
    this.awayScore.textContent = String(tick.match.awayScore);

    if (tick.match.homeScore !== this.lastHomeScore) {
      this.lastHomeScore = tick.match.homeScore;
      bump(this.homeScore);
    }
    if (tick.match.awayScore !== this.lastAwayScore) {
      this.lastAwayScore = tick.match.awayScore;
      bump(this.awayScore);
    }

    const balance = (tick.match.homeMomentum - tick.match.awayMomentum) / 2;
    const width = Math.min(50, Math.abs(balance) * 50);
    this.momentum.style.width = `${width}%`;
    this.momentum.style.left = balance >= 0 ? '50%' : `${50 - width}%`;
  }

  showSubtitle(text: string, intensity: CommentaryIntensity): void {
    this.subtitle.textContent = text;
    this.subtitle.className = `subtitle visible ${intensity}`;
    // Roughly reading speed, floored so a three-word line is still legible.
    this.subtitleTimer = Math.min(9, Math.max(2.6, text.length / 12));
  }

  update(deltaSeconds: number): void {
    this.clock.textContent = formatTimeOfDay(new Date());

    this.phaseRemainingMs = Math.max(0, this.phaseRemainingMs - deltaSeconds * 1000);
    this.phaseTimer.textContent = formatClock(this.phaseRemainingMs);

    this.sessionRemainingMs = Math.max(0, this.sessionRemainingMs - deltaSeconds * 1000);
    this.royaleSessionTimer.textContent = formatClock(this.sessionRemainingMs);

    if (this.nextSessionMs !== null) {
      this.nextSessionMs = Math.max(0, this.nextSessionMs - deltaSeconds * 1000);
      this.royaleNextTimer.textContent = formatClock(this.nextSessionMs);
    }

    if (this.subtitleTimer > 0) {
      this.subtitleTimer -= deltaSeconds;
      if (this.subtitleTimer <= 0) this.subtitle.classList.remove('visible');
    }

    this.scrollTicker(deltaSeconds);
  }

  private renderMatch(state: BroadcastState): void {
    const match = state.match;
    if (!match) {
      this.scorebug.classList.remove('visible');
      return;
    }

    this.scorebug.classList.add('visible');
    this.roundLabel.textContent = state.phase.shortLabel;
    this.homeCode.textContent = match.home.code;
    this.awayCode.textContent = match.away.code;
    this.homeName.textContent = countryByCode(match.home.code)?.name ?? '';
    this.awayName.textContent = countryByCode(match.away.code)?.name ?? '';
    this.lastHomeScore = match.home.score;
    this.lastAwayScore = match.away.score;
    this.homeScore.textContent = String(match.home.score);
    this.awayScore.textContent = String(match.away.score);
  }

  private updatePhaseRail(order: number): void {
    this.pips.forEach((pip, index) => {
      pip.classList.toggle('done', index + 1 < order);
      pip.classList.toggle('current', index + 1 === order);
      if (index + 1 !== order) pip.style.removeProperty('--pip-progress');
    });
  }

  // ------------------------------------------------------------ battle royale

  private renderRoyale(royale: BroadcastState['royale']): void {
    if (!royale) return;

    this.sessionRemainingMs = royale.sessionRemainingMs;
    this.nextSessionMs = royale.nextSessionInMs;
    this.flagsTotal = royale.totalFlags;

    // Write the clocks now as well as on the next frame. A snapshot has to leave
    // the DOM correct on its own: an OBS source in a hidden scene stops getting
    // animation frames, and it must not come back showing 00:00.
    this.royaleSessionTimer.textContent = formatClock(this.sessionRemainingMs);
    if (this.nextSessionMs !== null) {
      this.royaleNextTimer.textContent = formatClock(this.nextSessionMs);
    }

    const isFinal = royale.roundLabel === 'LAST FLAG STANDING';
    this.royaleRound.classList.toggle('is-final', isFinal);
    this.royaleRoundNo.textContent = `ROUND ${royale.round}`;
    this.royaleRoundLabel.textContent = royale.roundLabel;
    this.royaleBoardTitle.textContent = isFinal ? 'IN THE FINAL' : 'QUALIFIED FOR FINAL';

    this.royaleFlagsTotal.textContent = String(royale.totalFlags);
    this.setFlagsRemaining(royale.aliveCount);

    this.royaleNext.classList.toggle('visible', royale.nextSessionInMs !== null);

    this.royaleBoard.classList.toggle('has-entries', royale.leaderboard.length > 0);
    this.royaleBoardList.replaceChildren(
      ...royale.leaderboard.slice(0, 8).map((entry, index) => {
        const li = document.createElement('li');
        if (index === 0) li.className = 'leader';

        const rank = document.createElement('span');
        rank.className = 'rank';
        rank.textContent = `#${index + 1}`;

        const code = document.createElement('span');
        code.className = 'code';
        code.textContent = entry.code;

        const name = document.createElement('span');
        name.className = 'name';
        name.textContent = countryByCode(entry.code)?.name ?? entry.code;

        const wins = document.createElement('span');
        wins.className = 'wins';
        wins.textContent = `${entry.wins} ${entry.wins === 1 ? 'win' : 'wins'}`;

        li.append(rank, code, name, wins);
        return li;
      }),
    );
  }

  private setFlagsRemaining(alive: number): void {
    if (this.flagsTotal <= 0) return;
    this.royaleFlagsAlive.textContent = String(alive);
    this.royaleFlagsFill.style.width = `${(alive / this.flagsTotal) * 100}%`;
  }

  /** Count events as they happen, between snapshots. */
  recordEliminations(count: number): void {
    this.eliminationOffset += count;
    this.renderStats();
  }

  recordMatchPlayed(): void {
    this.matchOffset += 1;
    this.eliminationOffset += 1;
    this.renderStats();
  }

  private renderStats(): void {
    const stats = this.stats;
    if (!stats) return;

    const cells: Array<[string, string]> = [
      ['SEASONS', String(stats.seasonsCompleted)],
      ['MATCHES', String(stats.matchesPlayed + this.matchOffset)],
      ['ELIMINATIONS', String(stats.totalEliminations + this.eliminationOffset)],
    ];

    if (stats.mostTitles) {
      cells.push(['MOST TITLES', `${stats.mostTitles.code} x${stats.mostTitles.titles}`]);
    }
    if (stats.biggestUpset) {
      cells.push([
        'BIGGEST UPSET',
        `${stats.biggestUpset.code} beat ${stats.biggestUpset.beat} (+${stats.biggestUpset.delta})`,
      ]);
    }

    this.statsStrip.replaceChildren(
      ...cells.map(([key, value]) => {
        const cell = document.createElement('dl');
        cell.className = 'stat-cell';
        const dt = document.createElement('dt');
        dt.textContent = key;
        const dd = document.createElement('dd');
        dd.textContent = value;
        cell.append(dt, dd);
        return cell;
      }),
    );
  }

  private renderChampions(state: BroadcastState): void {
    const pills = state.champions.slice(0, 8).map((champion) => {
      const pill = document.createElement('div');
      pill.className = 'champion-pill';
      const code = document.createElement('b');
      code.textContent = champion.code;
      const label = document.createElement('span');
      label.textContent = `S${champion.seasonNumber}`;
      pill.append(code, label);
      return pill;
    });

    if (pills.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'champion-pill';
      empty.textContent = 'NO CHAMPION YET';
      pills.push(empty);
    }

    this.championList.replaceChildren(...pills);
  }

  /** Append new ticker entries without restarting the scroll. */
  pushTicker(items: readonly TickerItem[]): void {
    for (const item of items) {
      if (this.tickerItems.some((existing) => existing.id === item.id)) continue;
      this.tickerItems.push(item);
      const node = document.createElement('span');
      node.className = `ticker-item ${item.kind}`;
      node.textContent = item.text;
      this.tickerTrack.appendChild(node);
      this.tickerNodes.push(node);
    }
    this.trimTicker();
  }

  private syncTicker(items: readonly TickerItem[]): void {
    if (this.tickerItems.length === 0) {
      this.pushTicker(items);
      return;
    }
    const known = new Set(this.tickerItems.map((i) => i.id));
    this.pushTicker(items.filter((i) => !known.has(i.id)));
  }

  private trimTicker(): void {
    while (this.tickerNodes.length > 24) {
      const node = this.tickerNodes.shift();
      this.tickerItems.shift();
      node?.remove();
    }
  }

  /**
   * Marquee scroll.
   *
   * Driven from the render loop rather than a CSS animation so that entries can
   * be appended mid-scroll without the track jumping back to its start.
   */
  private scrollTicker(deltaSeconds: number): void {
    if (this.tickerNodes.length === 0) return;

    this.tickerOffset -= TICKER_SPEED * deltaSeconds;

    const first = this.tickerNodes[0];
    if (first) {
      const width = first.offsetWidth + 46;
      if (-this.tickerOffset > width) {
        // Recycle the leading item to the end and pull the track back by its
        // width, which keeps the visible content perfectly still.
        this.tickerOffset += width;
        this.tickerTrack.appendChild(first);
        this.tickerNodes.push(this.tickerNodes.shift()!);
        const item = this.tickerItems.shift();
        if (item) this.tickerItems.push(item);
      }
    }

    this.tickerTrack.style.transform = `translateX(${this.tickerOffset.toFixed(1)}px)`;
  }

  /**
   * The chrome is authored at the design size and scaled to fit with one
   * transform, so every offset in the stylesheet is a real broadcast pixel at
   * any output resolution - 720p preview, 1080p stream or 4K capture.
   */
  private applyScale(): void {
    const scale = Math.min(window.innerWidth / DESIGN.width, window.innerHeight / DESIGN.height);
    this.root.style.setProperty('--design-w', `${DESIGN.width}px`);
    this.root.style.setProperty('--design-h', `${DESIGN.height}px`);
    this.root.style.setProperty('--hud-scale', String(scale));
  }
}

function bump(node: HTMLElement): void {
  node.classList.remove('bump');
  // Force a reflow so the class can be re-applied on consecutive strikes.
  void node.offsetWidth;
  node.classList.add('bump');
  window.setTimeout(() => node.classList.remove('bump'), 240);
}
